/**
 * useTextEditing - Hook for inline text editing in text components
 *
 * Enables users to double-click a text component to enter edit mode,
 * type directly, apply formatting, and save changes - all while preserving
 * the existing AI chat functionality.
 *
 * Features:
 * - Double-click to enter edit mode
 * - contentEditable for native browser editing
 * - Auto-save on blur (clicking outside)
 * - Escape to cancel edits
 * - Keyboard shortcuts for formatting (Ctrl+B/I/U)
 * - Rich text segment preservation
 */

import { useCallback, useEffect, useRef } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import type { TextSegment } from '@/types/ai';

/**
 * Options for useTextEditing hook
 */
export interface UseTextEditingOptions {
  /** The component ID being edited */
  componentId: string;
  /** Initial text content (plain string or TextSegment[]) */
  initialContent: string | TextSegment[];
  /** Whether editing is enabled (typically only in editor mode) */
  enabled?: boolean;
  /** Callback when user saves edits */
  onSave: (newContent: string | TextSegment[]) => void;
  /** Callback when user cancels edits */
  onCancel?: () => void;
}

/**
 * Return type for useTextEditing hook
 */
export interface UseTextEditingResult {
  /** Whether this component is currently in edit mode */
  isEditing: boolean;
  /** Ref to attach to the editable element */
  editableRef: React.RefObject<HTMLDivElement>;
  /** Start editing mode */
  startEditing: () => void;
  /** Stop editing mode (with optional save) */
  stopEditing: (save: boolean) => void;
  /** Handle keyboard events during editing */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Handle input events during editing */
  handleInput: (e: React.FormEvent) => void;
  /** Handle blur event (auto-save) */
  handleBlur: (e: React.FocusEvent) => void;
  /** Handle double-click to enter edit mode */
  handleDoubleClick: (e: React.MouseEvent) => void;
}

/**
 * Hook for inline text editing with contentEditable
 */
export function useTextEditing({
  componentId,
  initialContent,
  enabled = true,
  onSave,
  onCancel,
}: UseTextEditingOptions): UseTextEditingResult {
  const editableRef = useRef<HTMLDivElement>(null);
  const localContentRef = useRef<string>('');
  const isBlurring = useRef(false); // Track blur to prevent double-save
  const justStartedEditing = useRef(false); // Prevent blur immediately after starting
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const latestEditingComponentIdRef = useRef<string | null>(null);
  const latestStopEditingRef = useRef<(save: boolean) => void>(() => {});

  // Get text editing state from editor store
  const textEditingComponentId = useEditorStore(state => state.textEditingComponentId);
  const setTextEditingComponent = useEditorStore(state => state.setTextEditingComponent);
  const selectComponent = useEditorStore(state => state.selectComponent);

  // Check if this component is currently being edited
  const isEditing = textEditingComponentId === componentId;

  const getTextOffset = useCallback((
    container: Node,
    targetNode: Node,
    targetOffset: number
  ): number => {
    let offset = 0;

    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    let node = walker.nextNode();
    while (node) {
      if (node === targetNode) {
        return offset + targetOffset;
      }
      offset += node.textContent?.length || 0;
      node = walker.nextNode();
    }

    return -1;
  }, []);

  const resolveTextPosition = useCallback((
    container: HTMLElement,
    absoluteOffset: number
  ): { node: Node; offset: number } | null => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    let remaining = absoluteOffset;
    let node = walker.nextNode();
    let lastTextNode: Node | null = null;

    while (node) {
      lastTextNode = node;
      const length = node.textContent?.length || 0;
      if (remaining <= length) {
        return { node, offset: remaining };
      }
      remaining -= length;
      node = walker.nextNode();
    }

    if (lastTextNode) {
      return {
        node: lastTextNode,
        offset: lastTextNode.textContent?.length || 0,
      };
    }

    return null;
  }, []);

  const captureCurrentSelection = useCallback((container: HTMLElement) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }

    const range = selection.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      return null;
    }

    const startOffset = getTextOffset(container, range.startContainer, range.startOffset);
    const endOffset = getTextOffset(container, range.endContainer, range.endOffset);

    if (startOffset === -1 || endOffset === -1) {
      return null;
    }

    return {
      start: Math.min(startOffset, endOffset),
      end: Math.max(startOffset, endOffset),
    };
  }, [getTextOffset]);

  const captureCaretFromPoint = useCallback((container: HTMLElement, x: number, y: number) => {
    const documentWithCaret = document as Document & {
      caretPositionFromPoint?: (clientX: number, clientY: number) => { offsetNode: Node; offset: number } | null;
      caretRangeFromPoint?: (clientX: number, clientY: number) => Range | null;
    };

    const caretPosition = documentWithCaret.caretPositionFromPoint?.(x, y);
    if (caretPosition && container.contains(caretPosition.offsetNode)) {
      const absoluteOffset = getTextOffset(container, caretPosition.offsetNode, caretPosition.offset);
      if (absoluteOffset !== -1) {
        return { start: absoluteOffset, end: absoluteOffset };
      }
    }

    const caretRange = documentWithCaret.caretRangeFromPoint?.(x, y);
    if (caretRange && container.contains(caretRange.startContainer)) {
      const absoluteOffset = getTextOffset(container, caretRange.startContainer, caretRange.startOffset);
      if (absoluteOffset !== -1) {
        return { start: absoluteOffset, end: absoluteOffset };
      }
    }

    return null;
  }, [getTextOffset]);

  /**
   * Convert initial content to plain text for editing
   */
  const getPlainText = useCallback((content: string | TextSegment[]): string => {
    if (typeof content === 'string') {
      return content;
    }
    if (Array.isArray(content)) {
      // TextSegment[] - concatenate all text
      return content.map(segment => segment.text).join('');
    }
    return '';
  }, []);

  /**
   * Start editing mode
   */
  const startEditing = useCallback(() => {
    if (!enabled) return;

    console.log('[useTextEditing] Starting edit mode for component:', componentId);

    // Set flag to prevent blur from firing immediately
    justStartedEditing.current = true;

    // Inline text editing should own selection. Collapsing multi-select here
    // prevents any leaked component-level delete shortcut from acting on
    // unrelated components while the user edits text.
    selectComponent(componentId);

    // Set this component as the active editing component
    setTextEditingComponent(componentId);

    // Initialize local content from initial content
    const plainText = getPlainText(initialContent);
    localContentRef.current = plainText;

    // Allow blur events after a delay (after focus is established)
    setTimeout(() => {
      justStartedEditing.current = false;
    }, 200);
  }, [enabled, componentId, initialContent, setTextEditingComponent, getPlainText, selectComponent]);

  /**
   * Stop editing mode (with optional save)
   */
  const stopEditing = useCallback((save: boolean) => {
    if (!isEditing) return;

    console.log('[useTextEditing] Stopping edit mode, save:', save);

    if (save && editableRef.current) {
      // Extract text content from contenteditable
      const newContent = editableRef.current.textContent || '';

      // Save the changes
      if (newContent !== getPlainText(initialContent)) {
        console.log('[useTextEditing] Content changed, saving:', newContent);
        onSave(newContent);
      }
    } else if (!save && onCancel) {
      // User cancelled
      onCancel();
    }

    // Clear the editing component and reset flags
    setTextEditingComponent(null);
    isBlurring.current = false;
    justStartedEditing.current = false;
  }, [isEditing, initialContent, onSave, onCancel, setTextEditingComponent, getPlainText]);

  useEffect(() => {
    latestEditingComponentIdRef.current = textEditingComponentId;
    latestStopEditingRef.current = stopEditing;
  }, [textEditingComponentId, stopEditing]);

  /**
   * Handle keyboard events during editing
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isEditing) return;

    // Workaround: insert space via the browser editing command first.
    // This behaves more reliably at text-node boundaries than direct
    // Range.insertNode(), especially when the caret is at offset 0.
    if (e.key === ' ') {
      const selection = window.getSelection();
      if (selection && editableRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const canUseInsertText =
          typeof document.execCommand === 'function' &&
          typeof document.queryCommandSupported === 'function' &&
          document.queryCommandSupported('insertText');

        if (canUseInsertText && document.execCommand('insertText', false, ' ')) {
          const inputEvent = new Event('input', { bubbles: true });
          editableRef.current.dispatchEvent(inputEvent);
          return;
        }

        const range = selection.getRangeAt(0);
        range.deleteContents();
        const spaceNode = document.createTextNode(' ');
        range.insertNode(spaceNode);
        range.setStartAfter(spaceNode);
        range.setEndAfter(spaceNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event manually
        const inputEvent = new Event('input', { bubbles: true });
        editableRef.current.dispatchEvent(inputEvent);

        return;
      }
    }

    const isMod = e.metaKey || e.ctrlKey;

    // Delete/Backspace - let contentEditable handle, but stop propagation
    // to prevent editor shortcuts from intercepting
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
      return; // Let contentEditable handle the actual deletion
    }

    // Arrow keys - let contentEditable handle, but stop propagation
    if (e.key.startsWith('Arrow')) {
      e.stopPropagation();
      return;
    }

    // Escape - cancel editing
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      stopEditing(false);
      return;
    }

    // Enter - save editing (unless Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      stopEditing(true);
      return;
    }

    // Formatting shortcuts - Ctrl/Cmd + B/I/U
    if (isMod && e.key === 'b') {
      e.preventDefault();
      e.stopPropagation();
      document.execCommand('bold');
      return;
    }

    if (isMod && e.key === 'i') {
      e.preventDefault();
      e.stopPropagation();
      document.execCommand('italic');
      return;
    }

    if (isMod && e.key === 'u') {
      e.preventDefault();
      e.stopPropagation();
      document.execCommand('underline');
      return;
    }

    // Select all text (not components) during editing
    if (isMod && e.key === 'a') {
      e.stopPropagation();
      return; // Let contentEditable handle select-all
    }

    // Copy/cut/paste text (not components) during editing
    if (isMod && (e.key === 'c' || e.key === 'x' || e.key === 'v')) {
      e.stopPropagation();
      return; // Let contentEditable handle copy/cut/paste
    }

    // All other keys - stop propagation to prevent editor shortcuts,
    // but let contentEditable handle them normally
    e.stopPropagation();
    return;
  }, [isEditing, stopEditing]);

  /**
   * Handle input events during editing
   */
  const handleInput = useCallback((e: React.FormEvent) => {
    if (!isEditing) return;

    localContentRef.current = (e.target as HTMLDivElement).textContent || '';
  }, [isEditing]);

  /**
   * Handle blur event (auto-save)
   */
  const handleBlur = useCallback((_e: React.FocusEvent) => {
    // Ignore blur if we just started editing (prevents immediate exit)
    if (!isEditing || isBlurring.current || justStartedEditing.current) return;

    // Check if we're actually losing focus or just re-rendering
    // If the element still has focus after a short delay, don't exit
    setTimeout(() => {
      // If the editable element still has focus, ignore this blur
      if (editableRef.current === document.activeElement) {
        return;
      }

      // Mark that we're blurring to prevent double-save
      isBlurring.current = true;

      console.log('[useTextEditing] Blur detected, auto-saving');

      // Small delay to allow other events to process first
      setTimeout(() => {
        stopEditing(true);
      }, 50);
    }, 50);
  }, [isEditing, stopEditing]);

  /**
   * Handle double-click to enter edit mode
   */
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;

    e.preventDefault();
    e.stopPropagation();

    const clickedCaret =
      captureCaretFromPoint(e.currentTarget as HTMLDivElement, e.clientX, e.clientY);
    const selectedRange = captureCurrentSelection(e.currentTarget as HTMLDivElement);

    pendingSelectionRef.current =
      clickedCaret ||
      (selectedRange
        ? { start: selectedRange.end, end: selectedRange.end }
        : null);

    console.log('[useTextEditing] Double-click detected');
    startEditing();
  }, [enabled, startEditing, captureCurrentSelection, captureCaretFromPoint]);

  /**
   * Focus the editable element and set initial content when entering edit mode
   */
  useEffect(() => {
    if (isEditing && editableRef.current) {
      // Set the text content directly (important for contentEditable to work properly)
      const plainText = getPlainText(initialContent);
      editableRef.current.textContent = plainText;

      // Focus after a small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (editableRef.current) {
          editableRef.current.focus();

          const range = document.createRange();
          const selection = window.getSelection();

          // Create a text node if none exists
          if (editableRef.current.childNodes.length === 0) {
            editableRef.current.textContent = plainText || ' '; // Add space if empty
          }

          const pendingSelection = pendingSelectionRef.current;
          if (pendingSelection) {
            const startPosition = resolveTextPosition(editableRef.current, pendingSelection.start);
            const endPosition = resolveTextPosition(editableRef.current, pendingSelection.end);

            if (startPosition && endPosition) {
              range.setStart(startPosition.node, startPosition.offset);
              range.setEnd(endPosition.node, endPosition.offset);
            }
            pendingSelectionRef.current = null;
          } else if (editableRef.current.childNodes.length > 0) {
            const lastNode = editableRef.current.childNodes[editableRef.current.childNodes.length - 1];
            const offset = lastNode.textContent?.length || 0;
            range.setStart(lastNode, offset);
            range.collapse(true);
          }

          if (selection && editableRef.current.childNodes.length > 0) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [isEditing, initialContent, getPlainText, resolveTextPosition]);

  /**
   * Clean up when component unmounts
   */
  useEffect(() => {
    return () => {
      // Only save on true unmount, not on ordinary rerenders where callbacks change identity.
      if (latestEditingComponentIdRef.current === componentId) {
        latestStopEditingRef.current(true);
      }
    };
  }, [componentId]);

  return {
    isEditing,
    editableRef,
    startEditing,
    stopEditing,
    handleKeyDown,
    handleInput,
    handleBlur,
    handleDoubleClick,
  };
}

export default useTextEditing;
